import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EmailIcon from '@mui/icons-material/MailOutlineRounded';
import { Box, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { ReactComponent as CallIcon } from '../../../assets/icons/call.svg';
import { ReactComponent as DashboardIcon } from "../../../assets/icons/dashboard-outlined.svg";
import { ReactComponent as FolderIcon } from '../../../assets/icons/folder.svg';
import theme from '../../../theme';
import { Dashboard, EmailBody, UnassignedContentType } from '../../../types/files';
import ContentSummaryAnswer from '../dashboard-query-answer/ContentSummaryAnswer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailSummaryModal from '../../modals/dashboard-queries/EmailSummaryModal';
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/linkedin.svg";

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        padding: 8,
        border: `1px ${theme.colors.neutral['400']} solid`,
        borderRadius: 12,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2)',
        transition: '0.05s ease-in-out',
        '&:hover': {
            border: `1px ${theme.colors.neutral['500']} solid`,
            borderRadius: 12,
            boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        }
    },
    contentIcon: {
        width: 26,
        height: 26,
    },
    title: {
        display: '-webkit-box',
        width: 'auto',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: theme.colors.neutral['700'],
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        textTransform: 'none',
        maxWidth: '32ch',
    },
    dashboard: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    subject: {
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        maxWidth: '40ch',
        wordBreak: 'break-all',
    },
    subject2: {
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#666666',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        maxWidth: '40ch',
        wordBreak: 'break-all',
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.colors.neutral['700'],
    },
    addTo: {
        paddingLeft: 16,
        paddingRight: 8,
        fontSize: '1rem',
        fontFamily: 'Inter',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
    },
    stageChip: {
        height: 30,
        width: 100,
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 500,
        backgroundColor: '#E0F1F1',
        color: theme.palette.primary.main,
    },
    lastUpdatedDate: {
        color: theme.colors.neutral['500'],
        fontSize: '0.8rem !important',
        whiteSpace: 'nowrap',
    },
    label: {
        color: theme.colors.neutral['600'],
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
    },
    ownership: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    owner: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    timestamp: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
        textAlign: 'right',
    },
    icon: {
        width: 28,
        height: 28,
    },
    seeEmail: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        color: theme.colors.neutral['500'],
    },
    url: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        textDecoration: 'none',
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        textAlign: 'left',
    },
}));

const ContentActivityFeed: React.FC<{
    activity: {
        dashboard: Dashboard,
        emailBody?: EmailBody & { summary?: string, threads?: ({ timestamp: string|null, sender?: string, message: string })[] },
        createdAt: string,
        type: 'attached' | 'created'
    },
    vertical?: boolean,
}> = ({ activity, vertical }) => {
    const classes = useStyles();
    const [isExpanded, setExpanded] = useState<boolean>(true);
    const [viewableEmail, setViewableEmail] = useState<(EmailBody & {summary : string})|null>(null);

    const { emailBody, createdAt, type } = activity;

    const getIcon = useCallback((type: UnassignedContentType) => {
        switch (type) {
            case UnassignedContentType.EMAIL:
            case UnassignedContentType.FULL_EMAIL:
                return <EmailIcon className={classes.contentIcon} sx={{ fill: '#7bd4d4 !important'}} />;
            case UnassignedContentType.LINKEDIN_MESSAGE:
                return  <LinkedInIcon style={{ width: 26, height: 26, fill: "#7bd4d4" }} />;
            case UnassignedContentType.CALL:
                return <CallIcon className={classes.contentIcon} stroke="#7bd4d4" />;
            case UnassignedContentType.DOCSEND:
                return  <DashboardIcon className={classes.contentIcon} fill={theme.palette.primary.main} />;
            case UnassignedContentType.OTHER:
            default:
                return <FolderIcon className={classes.contentIcon} fill="#7bd4d4" />;
        }
        // eslint-disable-next-line
    }, []);

    return (<>
        <Stack direction="column" spacing={1} width="100%" pl={1} py={1}>
            {!vertical && (<Divider sx={{ width: '100%' }} />)}
            {(type === 'created') ? (<>
                <Stack direction="row" spacing={1} className={classes.container} alignItems="center" width="100%" height="80px">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%">
                        <Box mt="4px">
                            <AddCircleOutlineIcon className={classes.contentIcon} sx={{ fill: '#7bd4d4!important' }} />
                        </Box>
                        <Typography className={classes.title}
                            fontSize="0.95rem !important"
                            color={`${theme.colors.neutral['500']} !important`}>
                            {'Company added to Notissia'}
                        </Typography>
                    </Stack>
                    <Typography className={classes.lastUpdatedDate}>{`${moment(createdAt).fromNow()}`}</Typography>
                </Stack>
            </>) : (type === 'attached') ? (<>
                <Stack direction="row" className={classes.container}
                    alignItems="center" justifyContent="space-between" width="100%" minHeight="100px">
                    <Stack direction="row" alignItems="flex-start" width="100%">
                        <Box mt="6px">
                            {getIcon(emailBody?.type as UnassignedContentType)}
                        </Box>
                        <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center" width="100%" py={1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" pl={1} width="100%">
                                <Typography className={classes.subject2} width="100%">
                                    {emailBody?.subject?.replace('Fwd:', '')?.replace('Fw:', '') || '<Untitled>'}
                                </Typography>
                                <Typography className={classes.timestamp} width="30%">
                                    {`${moment(emailBody?.createdAt).fromNow()}`}
                                </Typography>
                            </Stack>
                            {(emailBody?.type !== UnassignedContentType.LINKEDIN_MESSAGE) && (
                                <Stack direction="row" alignItems="center" justifyContent="space-between" pl={1}  width="100%">
                                    <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                        <Typography className={classes.ownership}>
                                            {'From: '}
                                            <Typography component="span" className={classes.owner} display="inline" pl="4px">
                                                {!!emailBody?.from ? emailBody.from.replace(/<.*?>/g, '').trim() : 'N/A'}
                                            </Typography>
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" justifyContent="flex-end">
                                        <IconButton sx={{ width: 24, height: 'auto', padding: 'unset' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setExpanded(prev => !prev);
                                            }} disableRipple>
                                            <ExpandMoreIcon style={{ transform: !isExpanded ? 'none' : 'rotate(180deg)' }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            )}
                            <Box width="100%" height="100%" pt={1}>
                                <Collapse in={isExpanded ?? false} collapsedSize={0}>
                                    <ContentSummaryAnswer content={emailBody!} onViewEmail={setViewableEmail} />
                                </Collapse>
                            </Box>
                        </Stack>
                    </Stack>
                </Stack>
            </>) : (<></>)}
        </Stack>
        {!!viewableEmail && (
            <EmailSummaryModal
                emailContent={viewableEmail}
                open={!!viewableEmail}
                onClose={() => setViewableEmail(null)} />
        )}
    </>);
}

export default ContentActivityFeed;
